// YouTube Ads AudienceLift Configuration - Alpine.js Component
// Handles configuration for AudienceLift YouTube advertisements

document.addEventListener('alpine:init', () => {
  Alpine.data('youtube_ads_audience_lift_x_data', () => ({
    // Component state
    isSubmitting: false,
    formData: {
      youtube_channel_link: '',
      targeting: {
        locations: []
      },
      episodes: []
    },
    
    // Input variables for text fields
    newLocation: '',
    
    // Validation state
    errors: {},
    
    init() {
      // Use $nextTick to ensure DOM is fully rendered before loading saved data
      this.$nextTick(() => {
        // Load saved configuration data if available
        this.loadSavedConfiguration();
        
        // Initialize with one episode row by default if no saved episodes
        if (this.formData.episodes.length === 0) {
          this.addEpisodeRow();
        }
        
        // Add a small delay to ensure DOM elements are fully rendered
        setTimeout(() => {
          this.initializeEpisodeSelect();
          // Ensure first episode row always has event listener bound
          this.ensureFirstEpisodeListener();
        }, 100);
      });
      
      // Set up cleanup when component is destroyed
      this.$cleanup = () => {
        this.cleanup();
      };
    },
    
    // Load saved configuration from backend
    loadSavedConfiguration() {
      try {
        const savedDataDiv = this.$refs.savedConfigData;
        
        if (savedDataDiv && savedDataDiv.dataset.savedConfig) {
          const savedConfig = JSON.parse(savedDataDiv.dataset.savedConfig);
          
          if (savedConfig) {
            // Load YouTube channel link
            if (savedConfig.youtube_channel_link) {
              this.formData.youtube_channel_link = savedConfig.youtube_channel_link;
            }
            
            // Load targeting data
            if (savedConfig.targeting) {
              // Handle both new array format and old string format for backward compatibility
              if (savedConfig.targeting.locations && Array.isArray(savedConfig.targeting.locations)) {
                this.formData.targeting.locations = savedConfig.targeting.locations;
              } else if (savedConfig.targeting.location && savedConfig.targeting.location !== '') {
                // Convert old string format to new array format
                this.formData.targeting.locations = [savedConfig.targeting.location];
              }
            }
            
            // Load episodes data
            if (savedConfig.episodes && savedConfig.episodes.length > 0) {
              // Clear existing episodes and load saved ones
              this.formData.episodes = [];
              
              savedConfig.episodes.forEach((savedEpisode, index) => {
                if (savedEpisode.episode_id) {
                  // Generate a unique ID for each episode row
                  const uniqueId = 'episode_' + Date.now() + '_' + index + '_' + Math.random().toString(36).substr(2, 9);
                  const newEpisode = {
                    id: uniqueId,
                    episode_id: savedEpisode.episode_id,
                    release_date: savedEpisode.release_date || ''
                  };
                  this.formData.episodes.push(newEpisode);
                }
              });
              
              // Populate episode select dropdowns with saved data
              this.$nextTick(() => {
                // Add a small delay to ensure DOM elements are fully rendered
                setTimeout(() => {
                  this.populateEpisodeSelects();
                  // Reinitialize event listeners after populating
                  this.initializeEpisodeSelect();
                }, 100);
              });
            }
          }
        }
      } catch (error) {
        // Silently handle configuration loading errors
      }
    },
    
    // Pill management functions for location targeting
    addLocation() {
      if (this.newLocation && this.newLocation.trim()) {
        const location = this.formatTextInput(this.newLocation.trim());
        if (location && !this.formData.targeting.locations.includes(location)) {
          this.formData.targeting.locations.push(location);
        }
        this.newLocation = '';
      }
    },

    removeLocation(location) {
      const index = this.formData.targeting.locations.indexOf(location);
      if (index > -1) {
        this.formData.targeting.locations.splice(index, 1);
      }
    },

    // Format text input: only allow letters, numbers, hyphens, and spaces between words
    formatTextInput(text) {
      return text
        .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters except letters, numbers, spaces, and hyphens
        .replace(/^[\s-]+|[\s-]+$/g, '') // Remove leading/trailing spaces and hyphens
        .replace(/[\s-]+/g, ' ') // Replace multiple spaces/hyphens with single space
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .trim();
    },
    
    // Initialize episode select event listeners
    initializeEpisodeSelect() {
      this.$nextTick(() => {
        // Add a small delay to ensure all DOM elements are fully rendered
        setTimeout(() => {
          // First, remove all existing event listeners from all episode selects
          this.formData.episodes.forEach(episode => {
            const selectElement = document.getElementById(`episode-select-${episode.id}`);
            if (selectElement) {
              // Remove any existing event listeners
              selectElement.removeEventListener('change', this.createEpisodeChangeHandler(episode.id));
            }
          });
          
          // Now bind event listeners to the current episode rows
          this.formData.episodes.forEach((episode, index) => {
            const selectElement = document.getElementById(`episode-select-${episode.id}`);
            if (selectElement) {
              // Add new event listener
              selectElement.addEventListener('change', this.createEpisodeChangeHandler(episode.id, index));
            }
          });
        }, 50);
      });
    },

    // Create a change handler for a specific episode select
    createEpisodeChangeHandler(episodeId, episodeIndex) {
      return (event) => {
        const selectedEpisodeId = event.target.value;
        
        if (selectedEpisodeId) {
          this.handleEpisodeSelection(selectedEpisodeId, episodeIndex);
        }
      };
    },
    
    // Ensure first episode row has event listener bound
    ensureFirstEpisodeListener() {
      if (this.formData.episodes.length > 0) {
        const firstSelectElement = document.getElementById('episode-select-0');
        if (firstSelectElement) {
          // Remove existing event listener to prevent duplicates
          firstSelectElement.removeEventListener('change', this.createEpisodeChangeHandler(0));
          // Add new event listener
          firstSelectElement.addEventListener('change', this.createEpisodeChangeHandler(0));
        }
      }
    },
    
    // Populate episode select dropdowns with saved data
    populateEpisodeSelects() {
      this.formData.episodes.forEach((episode, index) => {
        if (episode.episode_id) {
          const selectElement = document.getElementById(`episode-select-${index}`);
          
          if (selectElement) {
            selectElement.value = episode.episode_id;
          }
        }
      });
    },
    
    // Handle episode selection
    handleEpisodeSelection(episodeId, index) {
      if (episodeId && this.formData.episodes[index]) {
        // Check if this episode is already selected in another row
        const isDuplicate = this.formData.episodes.some((episode, episodeIndex) => 
          episodeIndex !== index && episode.episode_id === episodeId
        );
        
        if (isDuplicate) {
          alert('This episode is already selected in another row. Please choose a different episode.');
          // Reset the select dropdown to empty using the episode's unique ID
          const selectElement = document.getElementById(`episode-select-${this.formData.episodes[index].id}`);
          if (selectElement) {
            selectElement.value = '';
          }
          // Clear the episode data for this row
          this.formData.episodes[index].episode_id = '';
          this.formData.episodes[index].release_date = '';
          return;
        }
        
        this.formData.episodes[index].episode_id = episodeId;
        
        // Find the selected episode data from the backend episodes
        const selectedEpisode = this.findEpisodeById(episodeId);
        
        if (selectedEpisode) {
          // Handle pub_date
          if (selectedEpisode.pub_date) {
            const formattedDate = this.formatDate(selectedEpisode.pub_date);
            this.formData.episodes[index].release_date = formattedDate;
          } else {
            this.formData.episodes[index].release_date = 'Release date not available';
          }
        }
      }
    },
    
    // Find episode by ID from the backend data
    findEpisodeById(episodeId) {
      // Get episodes data from the global window object
      let episodesData = window.episodesData || [];
      
      if (!episodesData || episodesData.length === 0) {
        return null;
      }
      
      return episodesData.find(episode => episode.id == episodeId);
    },
    
    // Format date for display
    formatDate(dateString) {
      if (!dateString) return 'Date not available';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid date';
        
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        return 'Date not available';
      }
    },
    
    // Add new episode row
    addEpisodeRow() {
      if (this.formData.episodes.length >= 5) {
        alert('Maximum of 5 episodes allowed');
        return;
      }
      
      // Generate a unique ID for the new episode row
      const uniqueId = 'episode_' + Date.now() + '_' + this.formData.episodes.length + '_' + Math.random().toString(36).substr(2, 9);
      
      const newEpisode = {
        id: uniqueId,
        episode_id: '',
        release_date: ''
      };
      
      this.formData.episodes.push(newEpisode);
      
      // Initialize event listeners for the new row
      this.$nextTick(() => {
        this.initializeEpisodeSelect();
      });
    },
    
    // Clear episode selection for a specific row
    clearEpisodeSelection(episodeId, index) {
      if (this.formData.episodes[index]) {
        this.formData.episodes[index].episode_id = '';
        this.formData.episodes[index].release_date = '';
        
        // Reset the select dropdown
        const selectElement = document.getElementById(`episode-select-${episodeId}`);
        if (selectElement) {
          selectElement.value = '';
        }
      }
    },
    
    // Remove episode row
    removeEpisodeRow(episodeId) {
      // Don't allow removing the last episode row
      if (this.formData.episodes.length <= 1) {
        return;
      }
      
      // Find the index of the episode to remove
      const indexToRemove = this.formData.episodes.findIndex(episode => episode.id === episodeId);
      
      if (indexToRemove === -1) {
        return; // Episode not found
      }
      
      // Remove the episode from the array by index
      this.formData.episodes.splice(indexToRemove, 1);
      
      // Force Alpine.js to update the template
      this.formData.episodes = [...this.formData.episodes];
      
      // Reinitialize event listeners after removing a row
      this.$nextTick(() => {
        this.initializeEpisodeSelect();
        // Ensure all remaining episode selections are valid
        this.validateEpisodeSelections();
      });
    },
    
    // Validate and clean up episode selections after row removal
    validateEpisodeSelections() {
      this.formData.episodes.forEach((episode, index) => {
        if (episode.episode_id) {
          // Check if this episode is still valid (exists in episodes data)
          const episodeExists = window.episodesData && window.episodesData.some(ep => ep.id == episode.episode_id);
          if (!episodeExists) {
            // Clear invalid episode selection
            this.clearEpisodeSelection(episode.id, index);
          }
        }
      });
    },
    
    // Validate form data
    validateForm() {
      this.errors = {};
      
      if (!this.formData.youtube_channel_link?.trim()) {
        this.errors.youtube_channel_link = 'YouTube channel link is required';
        alert('YouTube channel link is required');
        return false;
      } else if (!this.isValidYouTubeUrl(this.formData.youtube_channel_link)) {
        this.errors.youtube_channel_link = 'Please enter a valid YouTube channel URL (e.g., https://www.youtube.com/@username or https://www.youtube.com/channel/...)';
        alert('Please enter a valid YouTube channel URL (e.g., https://www.youtube.com/@username or https://www.youtube.com/channel/...)');
        return false;
      }
      
      if (this.formData.targeting.locations.length === 0) {
        this.errors.location = 'At least one location is required';
        alert('At least one location is required');
        return false;
      }
      
      // Validate that at least one episode is selected
      const hasSelectedEpisode = this.formData.episodes.some(ep => ep.episode_id);
      if (!hasSelectedEpisode) {
        this.errors.episodes = 'At least one episode must be selected';
        alert('At least one episode must be selected');
        return false;
      }
      
      return true;
    },
    
    // Validate YouTube URL format - only allow channel URLs
    isValidYouTubeUrl(url) {
      // Allow YouTube channel URLs (channel/, c/, user/, and @username formats)
      return /^https:\/\/(www\.)?(youtube\.com\/(channel\/[A-Za-z0-9_-]+|c\/[A-Za-z0-9_-]+|user\/[A-Za-z0-9_-]+|@[A-Za-z0-9._-]+))$/.test(url);
    },
    
    // Save form configuration
    saveForm() {
      if (!this.validateForm()) {
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        // Create a hidden form and submit it
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = this.$refs.routes.dataset.submitLink;
        
        // Add CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (csrfToken) {
          const csrfInput = document.createElement('input');
          csrfInput.type = 'hidden';
          csrfInput.name = 'authenticity_token';
          csrfInput.value = csrfToken;
          form.appendChild(csrfInput);
        }
        
        // Add method override for PATCH
        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        methodInput.value = 'PATCH';
        form.appendChild(methodInput);
        
        // Prepare form data for submission
        const formData = {
          youtube_channel_link: this.formData.youtube_channel_link,
          targeting: {
            locations: this.formData.targeting.locations
          },
          episodes: this.formData.episodes.map(episode => ({
            episode_id: episode.episode_id,
            release_date: episode.release_date
          }))
        };
        
        // Add form data as hidden input
        const dataInput = document.createElement('input');
        dataInput.type = 'hidden';
        dataInput.name = 'form_data';
        dataInput.value = JSON.stringify(formData);
        form.appendChild(dataInput);
        
        // Submit the form
        document.body.appendChild(form);
        form.submit();
        
      } catch (error) {
        console.error('Error saving configuration:', error);
        alert('Failed to save configuration. Please try again.');
        this.isSubmitting = false;
      }
    },

    // Save as draft (no validations)
    saveAsDraft() {
      this.isSubmitting = true;
      
      try {
        // Create a hidden form and submit it
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = this.$refs.routes.dataset.saveAsDraftLink;
        
        // Add CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (csrfToken) {
          const csrfInput = document.createElement('input');
          csrfInput.type = 'hidden';
          csrfInput.name = 'authenticity_token';
          csrfInput.value = csrfToken;
          form.appendChild(csrfInput);
        }
        
        // Add method override for PATCH
        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        methodInput.value = 'PATCH';
        form.appendChild(methodInput);
        
        // Prepare form data for submission (same as saveForm but without validations)
        const formData = {
          youtube_channel_link: this.formData.youtube_channel_link,
          targeting: {
            locations: this.formData.targeting.locations
          },
          episodes: this.formData.episodes.map(episode => ({
            episode_id: episode.episode_id,
            release_date: episode.release_date
          }))
        };
        
        // Add form data as hidden input
        const dataInput = document.createElement('input');
        dataInput.type = 'hidden';
        dataInput.name = 'form_data';
        dataInput.value = JSON.stringify(formData);
        form.appendChild(dataInput);
        
        // Submit the form
        document.body.appendChild(form);
        form.submit();
        
      } catch (error) {
        console.error('Error saving draft:', error);
        alert('Failed to save draft. Please try again.');
        this.isSubmitting = false;
      }
    },
    
    // Helper functions to get IDs
    getShowId() {
      // Get show_id from backend via template tag using x-ref
      const template = this.$root.querySelector('[x-ref="show_data"]');
      return template ? template.dataset.showId : null;
    },

    getCampaignId() {
      // Get campaign_id from backend via template tag using x-ref
      const template = this.$root.querySelector('[x-ref="show_data"]');
      return template ? template.dataset.campaignId : null;
    },

    getCampaignTacticId() {
      // Get campaign_tactic_id from backend via template tag using x-ref
      const template = this.$root.querySelector('[x-ref="show_data"]');
      return template ? template.dataset.campaignTacticId : null;
    }
  }));
});
